/**
 * AerialTrackTargetAction - behavior tree action for aerial target tracking.
 */

import * as THREE from 'three';

import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface AerialTrackTargetActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  lastKnownPositionKey?: string;
  updateInterval?: number;
}

@EngineClass('AerialTrackTargetAction')
export class AerialTrackTargetAction extends BehaviorAction {
  private targetActorKey: string;
  private lastKnownPositionKey: string;
  private updateInterval: number;
  private lastUpdateTime: number = 0;

  constructor(options: AerialTrackTargetActionOptions = {}) {
    super({
      name: 'AerialTrackTarget',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.lastKnownPositionKey = options.lastKnownPositionKey ?? 'LastKnownPlayerPosition';
    this.updateInterval = options.updateInterval ?? 0.5; // Update every 0.5 seconds
  }

  protected override onInitialize(blackboard: Blackboard): void {}

  protected override onEnter(blackboard: Blackboard): void {
    this.lastUpdateTime = 0;
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const currentTime = Date.now() / 1000;

    if (currentTime - this.lastUpdateTime >= this.updateInterval) {
      const targetActor = blackboard.getValue<Actor>(this.targetActorKey);
      if (targetActor) {
        const targetPosition = new THREE.Vector3();
        targetActor.rootComponent.getWorldPosition(targetPosition);

        blackboard.setValue(this.lastKnownPositionKey, targetPosition);
        blackboard.setValue(`${this.lastKnownPositionKey}_Timestamp`, currentTime);

        this.lastUpdateTime = currentTime;
      }
    }

    return BehaviorStatus.Success;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {}
}
